import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { collection, getDocs, getDoc, doc, writeBatch } from 'firebase/firestore';
import Nav from './Nav';
import { getConnections, sendConnect, removeConnect, getMyBlocks } from './connections';
import { splitTile } from './tilePrivacy';

function genId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function Avatar({ user, size = 44 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
      background: 'var(--card-2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, color: 'var(--accent)', fontSize: size * 0.4,
    }}>
      {user.photoURL
        ? <img src={user.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : (user.displayName?.[0]?.toUpperCase() || '?')}
    </div>
  );
}

function PersonRow({ user, action, onNavigate }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
      borderBottom: '1px solid var(--border)',
    }}>
      <div onClick={() => onNavigate(user.username)} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, cursor: 'pointer' }}>
        <Avatar user={user} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.displayName}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>@{user.username}</div>
        </div>
      </div>
      {action}
    </div>
  );
}

function PeoplePage({ currentUser, currentUserProfile }) {
  const navigate = useNavigate();
  const myUid = currentUser?.uid;

  const [allUsers, setAllUsers] = useState([]);
  const [connStatus, setConnStatus] = useState({}); // otherUid -> status
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState({});   // username -> 'busy' | 'added' | 'full' | 'dup'
  const [connBusy, setConnBusy] = useState({}); // otherUid -> bool, connect button in-flight

  useEffect(() => {
    const load = async () => {
      try {
        const [snap, blocks] = await Promise.all([
          getDocs(collection(db, 'users')),
          getMyBlocks(myUid),
        ]);
        const users = snap.docs
          .map(d => ({ uid: d.id, ...d.data() }))
          .filter(u => u.username && u.uid !== myUid && !blocks.has(u.uid))
          .map(u => ({ uid: u.uid, username: u.username, displayName: u.displayName || u.username, photoURL: u.photoURL || null }));
        setAllUsers(users);
        if (myUid) setConnStatus(await getConnections(myUid));
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [myUid]);

  const q = query.trim().toLowerCase();
  const results = q
    ? allUsers.filter(u =>
        u.username.toLowerCase().includes(q) || u.displayName.toLowerCase().includes(q)
      ).slice(0, 25)
    : [];

  const connections = allUsers.filter(u => connStatus[u.uid] === 'accepted');

  // Usernames already in my Top 8, so we don't offer "Add to Top 8" for them.
  const inTop8 = new Set((currentUserProfile?.top8 || []).map(t => t.yt8Username).filter(Boolean));

  const addToTop8 = async (user) => {
    if (!myUid) return;
    setAdding(s => ({ ...s, [user.username]: 'busy' }));
    try {
      const meDoc = await getDoc(doc(db, 'users', myUid));
      const top8 = meDoc.data()?.top8 || [];
      if (top8.length >= 8) { setAdding(s => ({ ...s, [user.username]: 'full' })); return; }
      if (top8.some(t => t.yt8Username === user.username)) { setAdding(s => ({ ...s, [user.username]: 'dup' })); return; }

      const tileId = genId();
      const { publicTile, privateAction } = splitTile({
        id: tileId,
        displayName: user.displayName,
        photoURL: user.photoURL || null,
        isRegisteredUser: true,
        yt8Username: user.username,
        primaryAction: 'PROFILE',
        primaryValue: user.username,
        connections: [{ id: genId(), type: 'PROFILE', value: user.username }],
        connectionStatus: 'accepted', // already connected, so no new request
      });
      const batch = writeBatch(db);
      batch.set(doc(db, 'users', myUid, 'privateTileActions', tileId), privateAction);
      batch.update(doc(db, 'users', myUid), { top8: [...top8, publicTile] });
      await batch.commit();
      setAdding(s => ({ ...s, [user.username]: 'added' }));
    } catch (e) {
      console.error('Add to Top 8 failed:', e);
      setAdding(s => ({ ...s, [user.username]: '' }));
    }
  };

  const addLabel = (username) => {
    switch (adding[username]) {
      case 'busy': return '...';
      case 'added': return '✓ Added';
      case 'full': return 'Top 8 full';
      case 'dup': return 'Already in';
      default: return 'Add to Top 8';
    }
  };

  // Connecting is independent of the Top 8 and has no cap. You can connect with
  // as many people as you like; only the Top 8 itself is limited to eight.
  const me = (currentUserProfile && myUid) ? {
    uid: myUid,
    username: currentUserProfile.username,
    displayName: currentUserProfile.displayName,
    photoURL: currentUserProfile.photoURL,
  } : null;

  const handleConnectToggle = async (user) => {
    if (!me) return;
    setConnBusy(s => ({ ...s, [user.uid]: true }));
    try {
      if (!connStatus[user.uid]) {
        await sendConnect(me, { uid: user.uid, username: user.username, displayName: user.displayName });
        setConnStatus(s => ({ ...s, [user.uid]: 'pending' }));
      } else {
        await removeConnect(myUid, user.uid);
        setConnStatus(s => { const n = { ...s }; delete n[user.uid]; return n; });
      }
    } catch (e) {
      console.error('Connect failed:', e);
    }
    setConnBusy(s => ({ ...s, [user.uid]: false }));
  };

  const connectLabel = (uid) => {
    if (connBusy[uid]) return '…';
    const s = connStatus[uid];
    if (s === 'accepted') return '✓ Connected';
    if (s === 'pending') return 'Pending';
    return 'Connect';
  };

  return (
    <>
      <Nav currentUser={currentUser} currentUserProfile={currentUserProfile} />
      <div className="wrap" style={{ paddingTop: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 16px' }}>People</h1>

        <input
          className="input"
          placeholder="Find people by name or @username"
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{ width: '100%', marginBottom: 20 }}
          autoFocus
        />

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <span className="spinner" style={{ width: 28, height: 28 }} />
          </div>
        ) : q ? (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 8 }}>
              Results
            </div>
            {results.length === 0
              ? <div style={{ color: 'var(--muted)', fontSize: 14, padding: '12px 0' }}>No one found for "{query}".</div>
              : results.map(u => (
                  <PersonRow key={u.uid} user={u} onNavigate={(un) => navigate(`/${un}`)}
                    action={
                      currentUser ? (
                        <button
                          className={`btn btn-sm${connStatus[u.uid] ? '' : ' btn-primary'}`}
                          style={{ flexShrink: 0, ...(connStatus[u.uid] ? { background: 'var(--card-2)', border: '1px solid var(--border)' } : {}) }}
                          disabled={connBusy[u.uid]}
                          onClick={() => handleConnectToggle(u)}
                          title={connStatus[u.uid] === 'accepted' ? 'Tap to disconnect' : connStatus[u.uid] === 'pending' ? 'Tap to cancel request' : 'Connect'}
                        >
                          {connectLabel(u.uid)}
                        </button>
                      ) : (
                        <Link to={`/${u.username}`} className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)', flexShrink: 0 }}>
                          View
                        </Link>
                      )
                    }
                  />
                ))}
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 8 }}>
              Your connections ({connections.length})
            </div>
            {connections.length === 0
              ? <div style={{ color: 'var(--muted)', fontSize: 14, padding: '12px 0' }}>
                  No connections yet. Search for people above and Connect from their profile.
                </div>
              : connections.map(u => (
                  <PersonRow key={u.uid} user={u} onNavigate={(un) => navigate(`/${un}`)}
                    action={
                      (inTop8.has(u.username) || adding[u.username] === 'added')
                        ? <span style={{ flexShrink: 0, fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>In your Top 8</span>
                        : <button
                            className="btn btn-sm btn-primary"
                            style={{ flexShrink: 0 }}
                            disabled={['busy', 'full', 'dup'].includes(adding[u.username])}
                            onClick={() => addToTop8(u)}
                          >
                            {addLabel(u.username)}
                          </button>
                    }
                  />
                ))}
          </div>
        )}
      </div>
    </>
  );
}

export default PeoplePage;
